import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardBody, Button, Spinner, Alert, Switch } from '@components/ui';
import { PlusIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useInvoices } from '../hooks/useInvoices';
import { InvoiceStatus } from '../types/invoice.types';
import { ProfileIncompleteModal } from './ProfileIncompleteModal';
import { companyService } from '@features/companies/services/companyService';
import { useAuthStore } from '@features/auth/store/authStore';
import { getHomeRouteForRole } from '@lib/utils/roleNavigation';
import { useInvoiceFilterStore, filterErrorInvoices } from '@/stores/invoiceFilterStore';
import { useSelectedCompany } from '@features/company/hooks/useSelectedCompany';

const statusColors = {
  [InvoiceStatus.DRAFT]: 'bg-secondary-100 text-secondary-800',
  [InvoiceStatus.PENDING]: 'bg-warning-100 text-warning-800',
  [InvoiceStatus.APPROVED]: 'bg-success-100 text-success-800',
  [InvoiceStatus.REJECTED]: 'bg-danger-100 text-danger-800',
};

const statusLabels = {
  [InvoiceStatus.DRAFT]: 'Borrador',
  [InvoiceStatus.PENDING]: 'Pendiente',
  [InvoiceStatus.APPROVED]: 'Aprobada',
  [InvoiceStatus.REJECTED]: 'Rechazada',
};

export function InvoiceListPage() {
  const [page, setPage] = useState(1);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isCheckingCertificates, setIsCheckingCertificates] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const homeRoute = getHomeRouteForRole(user?.role);
  const { data: selectedCompanyData } = useSelectedCompany();
  const companyId = selectedCompanyData?.company?.id;
  const { data, isLoading, error, refetch } = useInvoices({ page, pageSize: 10, companyId });

  // Global filter state (persists across views)
  const { hideErrorInvoices, setHideErrorInvoices } = useInvoiceFilterStore();

  // Filter invoices to hide those with errors if toggle is on (uses global store)
  const filteredInvoices = filterErrorInvoices(data?.invoices || [], hideErrorInvoices);

  // Antes de crear una factura, verifica que el perfil de la compañía esté completo
  // (certificados). Si no lo está, muestra el modal "Perfil Incompleto".
  const handleNewInvoiceClick = async () => {
    setIsCheckingCertificates(true);

    const checkWithRetry = async (retries = 2): Promise<void> => {
      try {
        const result = await companyService.checkClientCertificates();

        if (result.hasCertificates) {
          // Perfil completo: ir directo a la creación de factura
          navigate('/invoices/new');
        } else {
          // Perfil incompleto: mostrar modal
          setShowProfileModal(true);
        }
      } catch (error: any) {
        console.error('Error checking certificates:', error);

        // Si el error es 400 (no hay compañía seleccionada), volver al home
        if (error?.response?.status === 400) {
          navigate(homeRoute);
          return;
        }

        // En errores transitorios, reintentar antes de rendirse
        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          return checkWithRetry(retries - 1);
        }

        // Agotados los reintentos, permitir continuar a la creación de factura
        console.warn('Could not verify certificates after retries, allowing user to proceed');
        navigate('/invoices/new');
      }
    };

    try {
      await checkWithRetry();
    } finally {
      setIsCheckingCertificates(false);
    }
  };

  const handleCompleteInfo = () => {
    setShowProfileModal(false);
    navigate('/company/settings?tab=fiscal');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    const err = error as any;
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.message || 'Error desconocido';
    return (
      <Alert variant="danger" title="Error al cargar facturas" data-testid="invoice-list-error-alert">
        <p>
          {status ? `[${status}] ` : ''}
          {message}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3" data-testid="invoice-list-retry-button">
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
            Facturas
          </h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Gestiona tus facturas electrónicas
          </p>
        </div>
        <Button
          leftIcon={<PlusIcon className="w-5 h-5" />}
          onClick={handleNewInvoiceClick}
          isLoading={isCheckingCertificates}
          data-testid="invoice-list-new-button"
        >
          Nueva Factura
        </Button>
      </div>

      {/* Filter controls */}
      <Card>
        <CardBody className="py-3 px-4">
          <div className="flex items-center justify-between">
            <Switch
              checked={hideErrorInvoices}
              onChange={setHideErrorInvoices}
              label="Ocultar facturas con error"
              size="sm"
              data-testid="invoice-list-hide-errors-switch"
            />
            {hideErrorInvoices && data?.invoices && (
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                {data.invoices.length - filteredInvoices.length} factura(s) con error oculta(s)
              </span>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-secondary-400" />
              {hideErrorInvoices && (data?.invoices?.length ?? 0) > 0 ? (
                <>
                  <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                    Todas las facturas de esta página tienen error
                  </h3>
                  <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                    Están ocultas por el filtro "Ocultar facturas con error". Avanza a la siguiente página o desactiva el filtro para verlas.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                    No hay facturas
                  </h3>
                  <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                    Comienza creando tu primera factura
                  </p>
                  <div className="mt-6">
                    <Button
                      variant="primary"
                      onClick={handleNewInvoiceClick}
                      isLoading={isCheckingCertificates}
                      data-testid="invoice-list-empty-new-button"
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      Nueva Factura
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700" data-testid="invoice-list-table">
                <thead className="bg-secondary-50 dark:bg-secondary-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Número
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                  {filteredInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
                      data-testid={`invoice-list-row-${invoice.id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900 dark:text-white">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-secondary-900 dark:text-white">
                          {invoice.customerName}
                        </div>
                        <div className="text-sm text-secondary-500 dark:text-secondary-400">
                          {invoice.customerRuc}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white">
                        {invoice.date && format(new Date(invoice.date), 'dd/MM/yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-secondary-900 dark:text-white">
                        ${(invoice.totalAmount ?? 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            invoice.status ? (statusColors as any)[invoice.status] : ''
                          }`}
                        >
                          {invoice.status && (statusLabels as any)[invoice.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/pages/invoice/${invoice.id}`}
                          className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400"
                          data-testid={`invoice-list-details-link-${invoice.id}`}
                        >
                          Ver detalles
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Paginación: se basa en el total del servidor (data.total), no en la lista
          ya filtrada por el toggle "Ocultar facturas con error" — así la paginación
          sigue siendo correcta aunque el filtro cliente oculte todas las filas de la
          página actual (ver AgilityTask cmrdzshw002xpg2fiad9caumc). */}
      {/*
        `data.total` es `number | null` — null cuando el backend aplica el
        camino de filtro client-side (searchApplied, ver InvoiceListResponse)
        y no conoce el total real sin agotar el dataset completo. Esta
        pantalla (legacy, sin cursor de escaneo) no soporta esa paginación
        incremental, así que mientras sea null se trata como "sin total
        conocido" en vez de romper el cálculo.
      */}
      {data && (data.total ?? 0) > 0 && (
        <Card>
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-secondary-700 dark:text-secondary-300">
                  Mostrando {(page - 1) * data.pageSize + 1} a{' '}
                  {Math.min(page * data.pageSize, data.total ?? 0)} de {data.total ?? 0} facturas
                </p>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">
                  Página {page} de {Math.ceil((data.total ?? 0) / data.pageSize)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  data-testid="invoice-list-prev-button"
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * data.pageSize >= (data.total ?? 0)}
                  onClick={() => setPage(page + 1)}
                  data-testid="invoice-list-next-button"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modal de perfil incompleto (certificados requeridos) */}
      <ProfileIncompleteModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onCompleteInfo={handleCompleteInfo}
      />
    </div>
  );
}
